import type { ActionFunctionArgs, ClientActionFunctionArgs } from 'react-router';
import { useEffect, useRef } from 'react';
import { redirect, Form, useActionData, useNavigation, useSearchParams } from 'react-router';
import { Button } from '@venncity/venn-ds';
import { get } from 'lodash-es';
import { Input, Label, Text, TextField } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { z } from 'zod';
import { getSession, sessionHeaders } from '~/lib/session.server';
import { queryClient } from '~/lib/cache.client';
import { createAnonymousVennGraphClient } from '~/lib/venn-graph-client.server';
import { handleExceptions } from '~/utils/http.server';
import { GlobalSearchParam } from '~/utils/search-params';
import { getOrganizationSubdomain } from '~/lib/auth.server';
import { initImpersonationSession } from '~/lib/impersonation-session.server';
import { IMPERSONATION_TOKEN_PARAM, readImpersonationPrefill } from '~/lib/impersonation-login';

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request);

  const url = new URL(request.url);
  const impersonationToken = url.searchParams.get(IMPERSONATION_TOKEN_PARAM);

  const formData = await request.formData();

  const formDataValue = formData.get('email');
  const email = typeof formDataValue === 'string' ? formDataValue.toLowerCase() : '';
  const timezone = formData.get('timezone');

  invariant(timezone, 'make sure you pass the user timezone via a hidden input');

  session.set(GlobalSearchParam.UserTimezone, timezone.toString());

  try {
    const EmailLoginSchema = z.object({
      email: z.string().email({ message: 'Invalid email address' }).min(1),
    });

    const validated = await EmailLoginSchema.parseAsync({ email });
    const client = createAnonymousVennGraphClient(request);
    const subdomain = await getOrganizationSubdomain(request);

    if (impersonationToken) {
      if (!subdomain) {
        throw new Error('Organization subdomain is required for impersonation');
      }
      return await initImpersonationSession({
        request,
        session,
        impersonationToken,
        usernameToImpersonate: validated.email,
        organizationSubdomain: subdomain,
      });
    }

    const { authChallengeCreate } = await client.mutation({
      authChallengeCreate: {
        __args: {
          data: {
            username: validated.email,
            organizationSubdomain: subdomain,
          },
        },
        organizationId: true,
        authSession: true,
        type: true,
      },
    });

    session.set('login-session', authChallengeCreate.authSession);
    session.set('login-method', authChallengeCreate.type);
    session.set('login-username', validated.email);

    return redirect(`/auth/verify`, { headers: await sessionHeaders(session) });
  } catch (error) {
    console.error('error while creating auth challenge', JSON.stringify(error));
    return await handleExceptions({
      error,
      request,
      flash: {
        title: 'Login failed, please try again',
        description: 'If the problem persists, please contact support.',
      },
    });
  }
}

// A new session begins: nothing cached for the previous user (the Hub's cached app data, say) may carry over.
export async function clientAction({ serverAction }: ClientActionFunctionArgs) {
  queryClient.clear();
  return serverAction();
}

export default function LoginEmail() {
  const { t } = useTranslation('auth-login');
  const navigation = useNavigation();
  const actionData = useActionData<typeof action>();
  const [searchParams] = useSearchParams();
  const errorMessage =
    actionData?.errors?.email?.[0] || get(actionData, 'message') || (actionData?.success === false ? t('email.login-failed') : undefined);

  const cta = searchParams.get(IMPERSONATION_TOKEN_PARAM) ? t('cta.impersonate') : t('cta.continue');
  const prefill = readImpersonationPrefill(searchParams);

  const isSubmitting = navigation.formMethod === 'POST' && (navigation.state === 'loading' || navigation.state === 'submitting');

  // A FrontDesk link names who to impersonate, so the form submits itself - through the same action,
  // so the timezone input, the exchange and the error flash all behave as a typed submit. Once per
  // mount (the ref survives StrictMode's double effect): after a failed exchange the page shows the
  // error and waits, rather than retrying a token that has just been refused.
  const formRef = useRef<HTMLFormElement>(null);
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (!prefill || autoSubmitted.current) return;
    autoSubmitted.current = true;
    formRef.current?.requestSubmit();
  }, [prefill]);

  return (
    <Form className="flex flex-col gap-4" method="POST" noValidate ref={formRef}>
      <input name="timezone" type="hidden" value={Intl.DateTimeFormat().resolvedOptions().timeZone} />
      <TextField className="flex flex-col" defaultValue={prefill?.username} isInvalid={!!errorMessage} name="email" type="email">
        <Label className="text-grey-900 pb-2 text-sm font-medium">{t('email.label')}</Label>
        <Input
          className="border-grey-300 text-grey-900 placeholder:text-grey-400 flex-1 rounded-lg border bg-white px-3 py-2 text-base font-medium focus:outline-none"
          placeholder={t('email.placeholder')}
        />
        {errorMessage && (
          <Text className="text-negative-500 mt-2 text-xs" slot="errorMessage">
            {errorMessage}
          </Text>
        )}
      </TextField>
      <Button block disabled={isSubmitting} htmlType="submit" loading={isSubmitting} shape="default" size="large" type="primary">
        {cta}
      </Button>
    </Form>
  );
}
